/**
 * Hook for managing image-related UI operations in the SMS editor.
 *
 * This hook is responsible for the UI layer of image handling, building on top
 * of the withImages plugin. The separation of concerns is as follows:
 *
 * This Hook's Responsibilities:
 * 1. UI State Management
 *    - Dialog visibility (showImageDialog)
 *    - Image presence tracking (hasImage)
 *    - Drag & drop visual feedback (isDragging)
 * 2. User Interactions
 *    - Handling file selection from dialog
 *    - Managing image deletion via overlay
 *    - Preventing typing after images
 *    - Visual feedback during drag & drop
 *
 * The Plugin Handles:
 * - All actual editor modifications
 * - URL management and cleanup
 * - File validation
 * - Drag & drop file handling
 *
 * This separation ensures that:
 * 1. Editor operations are centralized in the plugin
 * 2. UI concerns are isolated in this hook
 * 3. URLs persist properly for undo/redo
 * 4. There's no duplication of core functionality
 */

import { useCallback, useState, useEffect, useMemo } from 'react';
import { Editor, Element as SlateElement, Transforms, Descendant } from 'slate';
import { CustomElement } from '../utils/element-renderer';
import { validateAndInsertImage, removeImage, cleanupImageUrls } from '../plugins/with-images';

interface UseImageHandlingReturn {
  hasImage: boolean;
  showImageDialog: boolean;
  setShowImageDialog: (show: boolean) => void;
  handleFileSelected: (file: File, externalUrl?: string) => void;
  handleDeleteImage: (element: CustomElement) => void;
  preventTypingAfterImage: (event: React.KeyboardEvent) => boolean;
  isDragging: boolean;
  dragAndDropProps: {
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

/**
 * Checks if the editor value contains any image elements.
 * Uses Slate's type system for proper type checking.
 */
function hasImageInValue(value: Descendant[]): boolean {
  return value.some((node) => SlateElement.isElement(node) && node.type === 'image');
}

/**
 * Custom hook to manage image-related UI operations in the Slate editor.
 * Delegates actual image handling to the withImages plugin.
 *
 * @param editor - Slate Editor instance
 * @param value - Current editor value
 * @param toast - Toast notification function
 * @param setErrorMessage - Function to set error message state
 * @returns Object containing image handling methods and state
 */
export function useImageHandling(
  editor: Editor,
  value: Descendant[],
  setErrorMessage: (message: string | null) => void
): UseImageHandlingReturn {
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    return () => {
      cleanupImageUrls();
    };
  }, []);

  const handleFileSelected = useCallback(
    (file: File, externalUrl?: string) => {
      if (validateAndInsertImage(editor, file, setErrorMessage, externalUrl)) {
        setShowImageDialog(false);
      }
    },
    [editor, setErrorMessage]
  );

  const handleDeleteImage = useCallback(
    (element: CustomElement) => {
      if (element.type === 'image') {
        removeImage(editor, element);

        // Ensure there's always a paragraph node for content
        if (editor.children.length === 0) {
          Transforms.insertNodes(editor, {
            type: 'paragraph',
            children: [{ text: '' }]
          } as CustomElement);
        }
      }
    },
    [editor]
  );

  const preventTypingAfterImage = useCallback(
    (event: React.KeyboardEvent) => {
      const { selection } = editor;
      if (!selection) return false;

      const matchingNodes = Editor.nodes(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === 'image',
        reverse: true,
        at: selection
      });

      const hasMatchingNode = !matchingNodes.next().done;
      if (hasMatchingNode) {
        event.preventDefault();
      }

      return hasMatchingNode;
    },
    [editor]
  );

  const dragAndDropProps = useMemo(
    () => ({
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.types.includes('Files')) {
          setIsDragging(true);
        }
      },
      onDragLeave: (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
      }
    }),
    []
  );

  const hasImage = useMemo(() => hasImageInValue(value), [value]);

  return {
    hasImage,
    showImageDialog,
    setShowImageDialog,
    handleFileSelected,
    handleDeleteImage,
    preventTypingAfterImage,
    isDragging,
    dragAndDropProps
  };
}
